import { Component, Input, OnInit } from '@angular/core';
import { ANNULUS_INJECTOR_ANNULAR_TYPE_ISOLATION_CASING_ID } from 'src/app/handover-constants';
import {
  AnnulusInjector,
  AnnulusInjectorCasingLoss,
  AnnulusInjectorPressure,
} from 'src/app/interfaces/annulus-space';
import { Handover } from 'src/app/interfaces/handover';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SectionTouched } from '../../shared/section-touched.class';

@Component({
  selector: 'app-handover-detail-injector-wells',
  templateUrl: './handover-detail-injector-wells.component.html',
  styleUrls: ['./handover-detail-injector-wells.component.scss'],
  standalone: false,
})
export class HandoverDetailInjectorWellsComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handover: Handover | undefined;
  @Input() readOnlyContent!: boolean;

  /**
   * Una sola presión declarada: la del anular entre Csg Guía y Csg Aislación.
   *
   * El anular del Casing Guía se quitó a pedido de negocio (Eguren, 21/09/2026):
   * está cementado en su totalidad, no tiene fluido y queda fuera del cabezal,
   * así que no admite presión. El anular se muestra como texto fijo, sin
   * desplegable, pero se sigue guardando con su annularTypeId para no cambiar
   * el contrato ni el modelo.
   */
  pressure: AnnulusInjectorPressure = {};
  casingLoss: AnnulusInjectorCasingLoss = {};
  originalPressure: AnnulusInjectorPressure = {};
  originalCasingLoss: AnnulusInjectorCasingLoss = {};
  isLoading = false;
  isDisabled = true;

  constructor(
    public handoverService: HandoverService,
    public dialogService: DialogService,
    public notificationService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getAnnulusInjector();
  }

  /**
   * Trae los datos guardados y los monta sobre la tarjeta por defecto.
   *
   * El backend devuelve sólo las filas que existen, y puede traer además filas
   * de otros tipos de anular cargadas antes de este cambio: se toma únicamente
   * la del Casing Aislación, que es la que representa el anular mostrado.
   */
  getAnnulusInjector(): void {
    if (this.handover?.handoverId == undefined) {
      this.setDefaultValues();
      return;
    }
    this.isLoading = true;
    this.handoverService
      .getAnnulusInjector(this.handover.handoverId)
      .subscribe((injector: AnnulusInjector) => {
        this.setDefaultValues();
        const saved = injector?.pressures?.find(
          (p: AnnulusInjectorPressure) =>
            p.annularTypeId == ANNULUS_INJECTOR_ANNULAR_TYPE_ISOLATION_CASING_ID
        );
        if (saved) {
          Object.assign(this.pressure, saved);
        }
        // Independiente de la presión: puede haber pérdida de espesor
        // cargada sin ninguna presión declarada.
        if (injector?.casingLoss) {
          this.casingLoss = injector.casingLoss;
        }
        this.setOriginalValues();
        this.isLoading = false;
      });
  }

  setDefaultValues(): void {
    this.pressure = {
      annularTypeId: ANNULUS_INJECTOR_ANNULAR_TYPE_ISOLATION_CASING_ID,
    };
    this.casingLoss = {};
  }

  setOriginalValues(): void {
    this.originalPressure = Object.assign({}, this.pressure);
    this.originalCasingLoss = Object.assign({}, this.casingLoss);
    this.isDisabled = true;
  }

  enableSaveButton(): void {
    this.isDisabled = !this.thereAreChanges();
    this.emitPendingChanges(!this.isDisabled);
  }

  thereAreChanges(): boolean {
    return (
      this.casingLoss.injectionPressure !=
        this.originalCasingLoss.injectionPressure ||
      this.casingLoss.maxThicknessLossPercentage !=
        this.originalCasingLoss.maxThicknessLossPercentage ||
      this.pressure.fluidDensity != this.originalPressure.fluidDensity ||
      this.pressure.maxSurfacePressure !=
        this.originalPressure.maxSurfacePressure
    );
  }

  /**
   * Normaliza los campos vacíos a null antes de postear.
   *
   * Un matInput que el usuario vacía con Backspace deja cadena vacía, no
   * null, y el Joi del backend rechaza el '' con 400 ("must be a number"),
   * que en pantalla aparece como un error genérico al grabar.
   */
  emptyToNull<T extends object>(value: T): T {
    const sanitized = { ...value } as Record<string, unknown>;
    Object.keys(sanitized).forEach((key) => {
      if (sanitized[key] === '') {
        sanitized[key] = null;
      }
    });
    return sanitized as T;
  }

  save(): void {
    if (this.handover?.handoverId == undefined) {
      return;
    }
    const dialogRef = this.dialogService.openConfirmDialog(
      'Pozos Inyectores',
      '¿Desea guardar los cambios realizados en Pozos Inyectores?'
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.handoverService
          .saveAnnulusInjector(this.handover!.handoverId!, {
            pressures: [this.emptyToNull(this.pressure)],
            casingLoss: this.emptyToNull(this.casingLoss),
          })
          .subscribe((savedInjector: AnnulusInjector) => {
            if (savedInjector) {
              this.notificationService.notify(
                'Los cambios en Pozos Inyectores se han guardado con éxito.',
                true
              );
              this.setOriginalValues();
              this.emitPendingChanges(false);
            } else {
              this.notificationService.notify(
                'Ocurrió un error al grabar los cambios en Pozos Inyectores.',
                false
              );
            }
          });
      }
    });
  }
}
